import { classNames } from '@panwds/tailwindcss';
import React, { useRef, useState, cloneElement, useEffect } from 'react';
import { mergeRefs } from '../../utils/refs/refs.esm.js';
import { usePopper } from '../../utils/usePopper.esm.js';
import { POPOVER_PLACEMENTS } from '../../utils/constants.esm.js';
import { useShouldRender } from '../../utils/useShouldRender.esm.js';
import { Button } from '../Button/Button.esm.js';
import '@panwds/icons';
import '../Icon/ImgIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import { TruncateText } from '../TruncateText/TruncateText.esm.js';
import { Body } from '../Typography/Body.esm.js';
import { Dropdown } from './Dropdown.esm.js';
import { selectedItemLabel, isDomElement } from './utils.esm.js';

/**
 * This component is like `Select` but always has searching enabled.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { SelectWithSearch } from "@panwds/react-ui";
 * ```
 */
function SelectWithSearch({ 'data-autofocus': dataAutofocus, button, buttonRef, fullWidth = false, selectedItem, dataMetrics, dataTestId, enableInitialFocus = false, disabled, defaultPlacement = 'bottom-start', darkMode, placeholder, customWidth, fitToItemWidth = false, listItemSize = 'md', itemsShown = 10, allowDraftSelections = false, ...rest }) {
    var _a, _b;
    const anchorRef = useRef(null);
    const { triggerRef, containerRef } = usePopper({
        placement: defaultPlacement,
        strategy: 'fixed',
        ...(fitToItemWidth && {
            modifiers: [
                {
                    name: 'sameWidth',
                    enabled: true,
                    fn: ({ state }) => {
                        // todo: potential bug
                        state.styles['popper'].width = `${state.rects.reference.width}px`;
                    },
                    phase: 'beforeWrite',
                    requires: ['computeStyles']
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: [
                            POPOVER_PLACEMENTS.BOTTOM_START,
                            POPOVER_PLACEMENTS.BOTTOM_END,
                            POPOVER_PLACEMENTS.TOP_START,
                            POPOVER_PLACEMENTS.TOP_END
                        ]
                    }
                }
            ]
        })
    });
    const [isOpen, setIsOpen] = useState(false);
    const shouldRender = useShouldRender(isOpen);
    const DefaultButton = (React.createElement(Button, { "data-autofocus": dataAutofocus, "data-testid": "select-trigger", fullWidth: fullWidth, isMenu: true, appearance: "tertiary", addClassName: classNames(rest.addButtonClassName, 'tw-text-left'), isOpen: isOpen },
        React.createElement(TruncateText, { addClassName: "tw-flex-auto" }, selectedItemLabel(selectedItem) ||
            (placeholder ? (React.createElement(Body, { appearance: "secondary", addClassName: "tw-text-xs" }, placeholder)) : ('')))));
    const addProps = isDomElement(button === null || button === void 0 ? void 0 : button.type.toString()) ? {} : { isOpen };
    const trigger = cloneElement(button || DefaultButton, {
        ...addProps,
        id: rest.toggleButtonId,
        'aria-labelledby': `${(_a = rest.labelId) !== null && _a !== void 0 ? _a : ''} ${(_b = rest.toggleButtonId) !== null && _b !== void 0 ? _b : ''}`,
        'aria-expanded': isOpen,
        'aria-haspopup': 'listbox',
        ref: mergeRefs([buttonRef, anchorRef, triggerRef]),
        onMouseUp: () => setIsOpen(!isOpen),
        onKeyDown: (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                setIsOpen(true);
            }
        },
        ...(customWidth && { style: { width: customWidth } }),
        disabled
    });
    useEffect(() => {
        if (enableInitialFocus) {
            setTimeout(() => setIsOpen(true));
        }
    }, []);
    return (React.createElement("div", { className: classNames(darkMode && 'dark'), ...(customWidth && { style: { width: customWidth } }) },
        trigger,
        shouldRender && (React.createElement(Dropdown, { ...rest, dataMetrics: dataMetrics, dataTestId: dataTestId, darkMode: darkMode, isOpen: isOpen, selectedItem: selectedItem, anchorRef: anchorRef, containerRef: containerRef, onOpenChange: setIsOpen, fullWidth: fullWidth, fitToItemWidth: fullWidth ? false : fitToItemWidth, listItemSize: listItemSize, itemsShown: itemsShown, allowDraftSelections: allowDraftSelections }))));
}

export { SelectWithSearch };
//# sourceMappingURL=SelectWithSearch.esm.js.map
